import { QueryClientProvider } from '@tanstack/react-query';
import { i18next } from '@pc/i18n';
import {
  BootLoader,
  createAppQueryClient,
  lazyWithReload,
  RouterErrorElement,
  MfaChallengePage,
  RequireMfaVerified,
} from '@pc/ui';
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { Suspense } from 'react';
import { initSupabase } from '@pc/db';
import { env } from './env';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ActiveOrgProvider } from './auth/ActiveOrgProvider';
import { SignInPage } from './auth/SignInPage';
import { RequireShopAccess } from './auth/RequireShopAccess';
import { ShopShell } from './shell/ShopShell';

// -------------------------------------------------------------------------
// Anonymous browsing policy (2026-05-20 operator decision, Option D):
//
// shop.peptideclients.com is intentionally NOT login-gated end-to-end.
// Visitors land directly on the in-app marketplace (`MarketplaceHomePage`)
// and can browse `/shops`, `/browse`, any `/<vendor>` profile, and any
// `/<vendor>/<product>` detail page without an account. Only ACTIONS
// (cart, checkout, follow, message, place order, return) require a
// session -- the action surfaces detect `!session` and either disable
// with a "sign in to ..." hint (the cart-style buttons) or render a
// pass-through link to `/sign-in?next=<here>` (the vendor profile
// follow/message buttons, see VendorProfilePage's anon branch). The
// auth-gated action routes (`/cart`, `/checkout`, `/inbox`, `/orders`,
// `/addresses`, `/following`, `/help`, `/purchase-orders`) still flow
// through `<RequireAuth />` + `<RequireShopAccess />` so a deep-link
// from a marketing email bounces through `/sign-in` and back.
//
// The previous root redirect (`PUBLIC_LANDING_URL =
// 'https://peptideclients.com/shop.html'`) is intentionally removed.
// Sending first-time visitors to the marketing page made the shop feel
// gated even for browsing, which it isn't.
// -------------------------------------------------------------------------

// `lazyWithReload` is React.lazy + automatic recovery from the
// stale-chunk failure mode every Vite SPA suffers from after a
// deploy. If a code-split chunk 404s because the user is on the old
// shell, the wrapper hard-reloads (debounced) so the user lands on
// the new build instead of seeing react-router's raw "Failed to
// fetch dynamically imported module" stack trace. See
// `packages/ui/src/lazyWithReload.ts`.
const MarketplaceHomePage = lazyWithReload(() => import('./pages/MarketplaceHomePage'));
const ShopsPage = lazyWithReload(() => import('./pages/ShopsPage'));
const BrowsePage = lazyWithReload(() => import('./pages/BrowsePage'));
const VendorProfilePage = lazyWithReload(() => import('./pages/VendorProfilePage'));
const ProductDetailPage = lazyWithReload(() => import('./pages/ProductDetailPage'));
const FollowingPage = lazyWithReload(() => import('./pages/FollowingPage'));
const InboxPage = lazyWithReload(() => import('./pages/InboxPage'));
const OrdersPage = lazyWithReload(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazyWithReload(() => import('./pages/OrderDetailPage'));
const ShopPoListPage = lazyWithReload(() => import('./pages/PoListPage'));
const ShopPoDetailPage = lazyWithReload(() => import('./pages/PoDetailPage'));
// Cart + checkout + address book + returns (buyer-side funnel parity).
const CartHubPage = lazyWithReload(() => import('./pages/CartHubPage'));
const VendorCartPage = lazyWithReload(() => import('./pages/VendorCartPage'));
const CheckoutPage = lazyWithReload(() => import('./pages/CheckoutPage'));
const AddressBookPage = lazyWithReload(() => import('./pages/AddressBookPage'));
const RequestReturnPage = lazyWithReload(() => import('./pages/RequestReturnPage'));
const HelpInboxPage = lazyWithReload(() => import('./pages/HelpInboxPage'));
const HelpThreadPage = lazyWithReload(() => import('./pages/HelpThreadPage'));
const NotFoundPage = lazyWithReload(() => import('./pages/NotFoundPage'));

// NOTE (Bug 1 fix, 2026-05-19): the public-storefront route /@<slug> is
// NOT served by this router. React Router v6's `compilePath` only treats
// `:name` as a dynamic segment when it directly follows `/` (regex
// `\/:([\w-]+)` in @remix-run/router); the literal `@` between `/` and
// `:` in `/@:slug` made the pattern a no-match, and the route silently
// fell through to the `*` catch-all -> RequireAuth -> /sign-in. We now
// pre-route at the `main.tsx` entry: URLs matching /@<slug> mount the
// minimal `PublicStorefrontApp` (no AuthProvider, no Supabase init)
// before this router is ever created. See apps/shop/src/main.tsx and
// apps/shop/src/PublicStorefrontApp.tsx.
//
// The same trap bit `apps/sell` on 2026-05-20 (analytics drill-down
// route `@:vendorSlug/products/analytics/:productSlug`). Full rule +
// ESLint guardrail now lives at
// `ai-rules/react-router-no-inline-at-param.md` so a third repeat
// fails CI before it ever reaches production.

initSupabase({
  url: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  storageKey: 'pc-shop.auth',
});

const queryClient = createAppQueryClient();

function RouteLoader() {
  return <BootLoader label={i18nLoading()} />;
}

// We can't call useTranslation() from a top-level Suspense fallback that
// renders before the React tree is ready, so we read directly from the
// i18next instance. Falls back to English if i18n hasn't bootstrapped.
function i18nLoading(): string {
  try {
    return i18next.t('app.loading', 'Loading');
  } catch {
    return 'Loading';
  }
}

function RequireAuth() {
  const { session, loading } = useAuth();
  const location = useLocation();
  // Render directly off the AuthProvider's settled state. The earlier
  // version of this gate kept a local `resolvedSession` state machine
  // that re-ran on every `session` reference change; combined with the
  // `SIGNED_IN`-on-every-visibility event that supabase-js emits, that
  // produced a one-frame `<RouteLoader />` flash on each tab focus --
  // which, because <RequireAuth/> is a route element, unmounted the
  // entire matched child tree (including the active page's local form
  // state). The AuthProvider itself owns both the optimistic
  // localStorage seed (see readPersistedSession in auth/AuthProvider.tsx)
  // and the "still bootstrapping" race; this gate just routes off the
  // result.
  if (loading) return <RouteLoader />;
  if (!session) {
    // Action route gate: every route nested under `<RequireAuth />` is
    // an action (cart, checkout, inbox, orders, addresses, following,
    // help, purchase-orders). Anonymous visitors browsing the site
    // never hit RequireAuth because the browsable routes (`/`, `/shops`,
    // `/browse`, `/:slug`, `/:slug/:productSlug`) sit OUTSIDE this
    // wrapper -- see the router below.
    //
    // We forward `?next=<current>` so the sign-in form can round-trip
    // the user back to whatever action they were trying to take (e.g.
    // a "Place order" CTA in the cart hub sends them through
    // /sign-in?next=/cart, then back to /cart on success).
    const next = location.pathname + location.search + location.hash;
    const search = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/sign-in${search}`} replace />;
  }
  return <Outlet />;
}


const router = createBrowserRouter([
  {
    // `RouterErrorElement` catches every render-time throw inside the
    // router (including code-split chunk failures from `lazyWithReload`
    // that didn't auto-recover within the debounce). Without it,
    // react-router falls back to its dev-style "Unexpected Application
    // Error!" UI that leaks the stack trace to end users — that's the
    // exact screen the operator reported on 2026-05-20 after the
    // marketplace-home deploy. See packages/ui/src/RouterErrorElement.tsx.
    errorElement: <RouterErrorElement homeHref="/" />,
    children: [
      { path: '/sign-in', element: <SignInPage /> },
      // /@<slug> is intentionally NOT a route here -- it's pre-routed in
      // main.tsx (see Bug 1 fix note above) so that public visitors never
      // load AuthProvider / initSupabase / RequireAuth at all.
      {
        // -----------------------------------------------------------
        // Top-level layout: ShopShell mounts for EVERY in-app route
        // (anon or signed-in). ShopShell reads `useAuth()` internally
        // to switch the nav cluster between the signed-in operator
        // chrome (Inbox / Help / Orders / Account / Cart) and the
        // signed-out browse chrome (Sign in / Sign up). Mounting the
        // shell at this single layer keeps the chrome from
        // remounting when a signed-in user navigates from a public
        // browse page to an auth-gated action page.
        // -----------------------------------------------------------
        element: <ShopShell />,
        children: [
          // ---- Public, browsable routes (anon-friendly) -------------
          //
          // No auth gate. The data layer (browseProducts,
          // listShopVendorsPublic, getShopVendorPublicBySlug,
          // getShopProductPublic) goes through SECURITY DEFINER RPCs
          // that are anon-callable; the in-page action surfaces
          // (Follow / Message / Add-to-cart / Request PO) detect
          // `!session?.user.id` and either disable with a hint or
          // swap to a "Sign in to ..." link to /sign-in?next=<here>.
          //
          // See ai-rules/feature-verification.md owner_paths on
          // docs/qa/cross-cutting/vendor-public-storefront.md.
          // -----------------------------------------------------------
          {
            index: true,
            element: (
              <Suspense fallback={<RouteLoader />}>
                <MarketplaceHomePage />
              </Suspense>
            ),
          },
          {
            path: 'browse',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <BrowsePage />
              </Suspense>
            ),
          },
          {
            path: 'shops',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <ShopsPage />
              </Suspense>
            ),
          },
          {
            path: ':slug',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <VendorProfilePage />
              </Suspense>
            ),
          },
          {
            path: ':slug/:productSlug',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <ProductDetailPage />
              </Suspense>
            ),
          },
          // ---- Auth-gated action routes -----------------------------
          //
          // Cart, checkout, inbox, orders, addresses, following, help,
          // purchase-orders -- each fundamentally requires a session
          // (and most require an active org via RequireShopAccess).
          // Anonymous visitors who click a NavLink to one of these
          // bounce through /sign-in?next=<path>.
          //
          // We nest the three gates under one route entry so each child
          // doesn't have to repeat them; the route tree stays flat at
          // the child level (one entry per route) to make the auth
          // boundary obvious in `git blame`.
          //
          // 2FA gate (`<RequireMfaVerified />`) sits BETWEEN
          // `<RequireAuth />` and `<RequireShopAccess />`:
          //   - users without a session still get bounced to /sign-in
          //     (RequireAuth's job, runs first);
          //   - users with a session but 2FA enabled + unverified
          //     bounce to /mfa where the shared `<MfaChallengePage />`
          //     completes the OTP / recovery code step;
          //   - users without 2FA pass straight through to the shop
          //     gate, then to the action route.
          // The `/mfa` route itself is mounted OUTSIDE
          // `<RequireMfaVerified />` to avoid the redirect loop
          // (gate -> /mfa -> gate -> /mfa -> ...).
          // -----------------------------------------------------------
          {
            element: <RequireAuth />,
            children: [
              { path: 'mfa', element: <MfaChallengePage /> },
              {
                element: <RequireMfaVerified />,
                children: [{
                element: <RequireShopAccess />,
                children: [
                  {
                    path: 'following',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <FollowingPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'inbox',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <InboxPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'inbox/:threadId',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <InboxPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'orders',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <OrdersPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'orders/:id',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <OrderDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'orders/:orderId/return',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <RequestReturnPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'cart',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <CartHubPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'cart/:vendorSlug',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <VendorCartPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'checkout/:vendorSlug',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <CheckoutPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'addresses',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <AddressBookPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'purchase-orders',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <ShopPoListPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'purchase-orders/:id',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <ShopPoDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'help',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <HelpInboxPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'help/:id',
                    element: (
                      <Suspense fallback={<RouteLoader />}>
                        <HelpThreadPage />
                      </Suspense>
                    ),
                  },
                ],
              }],
              },
            ],
          },
        ],
      },
      {
        // Catch-all 404. Replaces the previous `<Navigate to="/" replace />`
        // which silently bounced bad URLs to the discover feed with no
        // explanation. Now a stale vendor profile, mistyped product link,
        // or unknown route surfaces our branded 404 instead.
        //
        // The public storefront pre-route in `main.tsx` (Bug 1 fix) still
        // owns valid `/@<slug>` URLs; a malformed slug there falls through
        // to this catch-all rather than the dead RequireAuth path, which
        // is the intended UX.
        path: '*',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ActiveOrgProvider>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </ActiveOrgProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
